import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInjection } from '../../../../../dependancyInjection/DependencyContext';
import DependencyType from '../../../../../dependancyInjection/DependencyType';
import { PanelHeader } from '../PanelHeader/PanelHeader';
import { ConfigurationService } from '../../../../../services/ConfigurationService/ConfigurationService';
import { BasketService } from '../../../../../services/BasketService/BasketService';
import {
    ContinuePurchaseFlowCallback,
    PurchaseStep,
    usePurchase,
} from '../../../../../hooks/contexts/Purchase/Purchase';
import { Category } from '../../../../../services/CategoryService/entities/Category';
import ResponsiveGrid, {
    generateGridProperties,
    ResponsiveGridItemWithKey,
} from '../../../../shared/ResponsiveGrid/ResponsiveGrid';
import { getDeviceDPI } from '../../../../../index';
import ProductCard, { BannerType } from '../ProductCard/ProductCard';
import { getSizedImageURL } from '../../../../../utils/ImageURL.Util';
import PriceService from '../../../../../services/PriceService/PriceService';
import {
    ProductFilteringService,
    ProductsFetchOptions,
} from '../../../../../services/ProductServices/ProductFilteringService';
import { ProductSearchResultWithCursor } from '../../../../../provider/cloudshelf/common/ProductSearchResultWithCursor';
import _ from 'lodash';
import AppConfig from '../../../../../types/AppConfig';
import { RoutesHelperService } from '../../../../../services/RoutesService/RoutesHelperService';
import { EventsService } from '../../../../../services/EventsService/EventsService';
import Modal from '../../../../shared/Modal/Modal';
import ProductVariants from '../ProductDetailsView/buildingBlocks/variants/ProductVariants';
import { LocalProduct, LocalProductVariant } from '../../../../../services/ProductServices/LocalProduct';
import Button, { ButtonOutlineVariant, ButtonVariant } from '../../../../shared/inputs/Button/Button';
import { TextSize } from '../../../../shared/StyledText/StyledText';
import CurrencyService from '../../../../../services/CurrencyService/CurrencyService';
import { useTranslation } from 'react-i18next';
import Quantity from '../Quantity/Quantity';
import { BasketItem } from '../../../../../services/BasketService/Basket.type';
import { FunctionalComponentWithChildren } from '../../../../../FCWithChildren';

export interface VariantSelectorModalProps {
    basketItem?: BasketItem;
    forProduct?: LocalProduct;
    currentVariant?: LocalProductVariant;
    onClose?: () => void;
    addToBasket?: (product: LocalProduct, variant: LocalProductVariant, quantity: number, updateUI: boolean) => void;
}

export const VariantSelectorModal: FunctionalComponentWithChildren<VariantSelectorModalProps> = props => {
    const [originalSelectedVariant, setOriginalSelectedVariant] = useState<LocalProductVariant | undefined>(undefined);
    const [originalQuantity, setOriginalQuantity] = useState<number | undefined>(undefined);
    const [currentQuantity, setCurrentQuantity] = useState<number | undefined>(undefined);
    const [productForVariantModal, setProductForVariantModal] = useState<LocalProduct | undefined>(undefined);
    const [selectedVariant, setSelectedVariant] = useState<LocalProductVariant | undefined>(undefined);
    const [currentBasketItem, setCurrentBasketItem] = useState<BasketItem | undefined>(undefined);
    const [loadingChanges, setLoadingChanges] = useState(false);
    const eventsService = useInjection<EventsService>(DependencyType.EventsService);
    const basketService = useInjection<BasketService>(DependencyType.BasketService);
    const configService = useInjection<ConfigurationService>(DependencyType.ConfigurationService);
    const { t } = useTranslation();
    const purchaseContext = usePurchase();
    const productCustomiserPriceModifierVariant = configService.productCustomiserPriceModifierVariant;

    useEffect(() => {
        if (props.forProduct) {
            setProductForVariantModal(props.forProduct);
        }
    }, [props.forProduct]);

    useEffect(() => {
        if (props.currentVariant) {
            setOriginalSelectedVariant(props.currentVariant);
            setSelectedVariant(props.currentVariant);
        }
    }, [props.currentVariant]);

    useEffect(() => {
        if (props.basketItem) {
            setCurrentBasketItem(props.basketItem);
            setCurrentQuantity(props.basketItem.quantity);
            setOriginalQuantity(props.basketItem.quantity);
        } else {
            setCurrentQuantity(1);
        }
    }, [props.basketItem]);

    if (productForVariantModal === undefined) {
        return null;
    }

    const closeModal = () => {
        setProductForVariantModal(undefined);
        setSelectedVariant(undefined);
        setCurrentBasketItem(undefined);
        props.onClose?.();
    };

    const useSalePrice = selectedVariant?.originalPrice !== selectedVariant?.price;

    const handleRemove = async () => {
        console.log('handle remove');
        if (!currentBasketItem || !selectedVariant) {
            return;
        }

        setLoadingChanges(true);
        await basketService.removeItem(currentBasketItem.variant, currentBasketItem.attributes);
        if (productCustomiserPriceModifierVariant) {
            await basketService.removeItem(productCustomiserPriceModifierVariant, currentBasketItem.attributes);
        }
        setLoadingChanges(false);
        closeModal();
    };

    let updateButtonEnabled = false;

    if (selectedVariant) {
        if (currentQuantity !== originalQuantity) {
            console.log('Update quantity', currentQuantity, originalQuantity);
            updateButtonEnabled = true;
        }

        if (selectedVariant.id !== originalSelectedVariant?.id) {
            console.log('Update variant', selectedVariant.id, originalSelectedVariant?.id);
            updateButtonEnabled = true;
        }
    }

    const handleCallCustomAddToBasket = async () => {
        if (!selectedVariant || !currentQuantity) {
            return;
        }
        setLoadingChanges(true);
        props.addToBasket?.(productForVariantModal, selectedVariant, currentQuantity, true);
        closeModal();
        setLoadingChanges(false);
    };

    const handleUpdate = async () => {
        if (!selectedVariant || !currentBasketItem || !originalSelectedVariant || !currentQuantity) {
            return;
        }

        setLoadingChanges(true);

        if (selectedVariant.id === originalSelectedVariant?.id) {
            // Update the quantity if the selected variant is the same
            await basketService.setItemQuantity(
                productForVariantModal,
                selectedVariant,
                currentQuantity,
                currentBasketItem.attributes,
                productForVariantModal?.title ?? selectedVariant.displayName,
            );
        } else {
            // Remove the original item from the basket
            await basketService.removeItem(originalSelectedVariant, currentBasketItem.attributes);

            // Add the new variant to the basket
            await basketService.setItemQuantity(
                productForVariantModal,
                selectedVariant,
                currentQuantity,
                currentBasketItem.attributes,
                productForVariantModal?.title ?? selectedVariant.displayName,
            );
        }

        setLoadingChanges(false);
        closeModal();
    };

    return (
        <Modal
            topMost
            isOpen={productForVariantModal !== undefined}
            displayVariant="compact"
            bodyClassName="VariantSelectorModal"
            onClose={closeModal}
            dontChangeMenu
        >
            <PanelHeader
                className={'VariantSelectorModal__Header'}
                onCloseButtonClicked={closeModal}
                title={productForVariantModal.title}
                textSize={TextSize.Medium}
                leftAlignTitle
            />
            <div className={'VariantSelectorModal__Content'}>
                <div className="VariantSelectorModal__Content__Variants">
                    <ProductVariants
                        key="VariantSelectorModal-variants"
                        reactKey="VariantSelectorModal-variants"
                        variants={productForVariantModal?.variants ?? []}
                        originalSelectedVariant={originalSelectedVariant}
                        onVariantSelected={setSelectedVariant}
                        onMatchingPartialVariant={setSelectedVariant}
                    />
                </div>
                <div className="VariantSelectorModal__Content__Quantity">
                    <div className="VariantSelectorModal__Content__Quantity__Text">{t('quantity.title')}</div>
                    <div className="VariantSelectorModal__Content__Quantity__Control">
                        <Quantity
                            className=""
                            initialQuantity={currentQuantity}
                            onQuantityChanged={setCurrentQuantity}
                            onRemove={props.basketItem ? handleRemove : undefined}
                            variant={'inline'}
                            useRemoveIcon
                            loading={loadingChanges}
                            overrideQuantity={currentQuantity}
                            maxQuantity={props.basketItem?.maxQuantity}
                        />
                    </div>
                </div>
            </div>
            <hr />
            <div className={'VariantSelectorModal__Footer'}>
                <div className="VariantSelectorModal__Footer__Price">
                    {selectedVariant && (
                        <>
                            {useSalePrice ? (
                                <>
                                    <div className="VariantSelectorModal__Footer__Price__CurrentPrice VariantSelectorModal__Footer__Price__Sale">
                                        {CurrencyService.format(selectedVariant.price)}
                                    </div>
                                    <div className="VariantSelectorModal__Footer__Price__OriginalPrice">
                                        {CurrencyService.format(selectedVariant.originalPrice)}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="VariantSelectorModal__Footer__Price__CurrentPrice">
                                        {CurrencyService.format(selectedVariant.price)}
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
                <div className="VariantSelectorModal__Footer__Buttons">
                    <Button
                        variant={ButtonVariant.GREY}
                        outline={ButtonOutlineVariant.PRIMARY}
                        disabled={!selectedVariant}
                        onClick={async () => {
                            purchaseContext.continue(PurchaseStep.NONE);
                            eventsService.setOpenProduct(productForVariantModal.handle);
                            setProductForVariantModal(undefined);
                        }}
                    >
                        {t('detail')}
                    </Button>
                    {props.addToBasket ? (
                        <Button disabled={false} loading={loadingChanges} onClick={handleCallCustomAddToBasket}>
                            {t('product_view.buy_buttons.add_to_basket')}
                        </Button>
                    ) : (
                        <Button disabled={!updateButtonEnabled} loading={loadingChanges} onClick={handleUpdate}>
                            {t('update')}
                        </Button>
                    )}
                </div>
            </div>
        </Modal>
    );
};
